import type React from 'react'

// Sidebar's stylesheet. Pure data, lifted out of the screen file so the
// component itself is navigable. No logic here.

export const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 'var(--sidebar-width)',
    height: '100dvh',
    // Use the design-token sidebar bg instead of a hardcoded near-black so
    // it stays in sync with the rest of the charcoal palette.
    background: 'var(--bg-sidebar)',
    borderRight: '1px solid var(--border-primary)',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px 14px calc(20px + env(safe-area-inset-bottom))',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100,
    // The aside itself never scrolls — the nav list scrolls internally
    // (see styles.nav). This keeps the logo pinned to the top and the brand
    // box pinned to the bottom-left corner instead of scrolling out of view.
    overflow: 'hidden',
  },
  top: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // Fill the space between the (pinned) brand box and the top edge, and
    // allow the nav child to shrink below its content height so it can scroll.
    flex: 1,
    minHeight: 0,
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '0px 8px 16px',
    // Divider between the logo and the nav sections below.
    borderBottom: '1px solid var(--border-primary)',
    // Stay pinned at the top — only the nav list below scrolls.
    flexShrink: 0,
  },
  // Brand row — refined 2026-05-02 round 2 per design feedback. The previous
  // version had a visible border + bg fill that read as "button container."
  // The reference shows a borderless flat row that sits in the sidebar like
  // identity, not a button. Tighter padding for a more compact footprint.
  // User/brand card — the bottom anchor. Given a real card treatment (border
  // + elevated surface) so it reads as an account chip rather than a flat row.
  // Hover tints purple (see .brand-row-button in globals.css).
  brandSwitcher: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-primary)',
    borderRadius: '10px',
    cursor: 'pointer',
    color: 'var(--text-primary)',
    transition: 'background 0.15s ease, border-color 0.15s ease',
    fontFamily: 'inherit',
  },
  // True circle avatar with the BMC brand gradient (pink → blue, mirrors the
  // BMC logo). 32px matches the reference's compact sizing better than 36
  // — gives the row a tighter overall footprint.
  brandAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #FB3AAE 0%, #4A90E2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 700,
    flexShrink: 0,
    letterSpacing: '-0.01em',
  },
  brandAvatarImg: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover' as const,
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
  },
  brandInfo: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left' as const,
  },
  brandName: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    letterSpacing: '-0.005em',
    lineHeight: '14px',
  },
  brandCaption: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-tertiary)',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '1px',
    textTransform: 'capitalize' as const,
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    // The one scrollable region: takes all remaining height between the
    // pinned logo and the pinned brand box, and scrolls when the nav is
    // taller than the viewport. minHeight:0 lets it shrink inside the flex
    // column (without it, flex children refuse to scroll).
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Small gutter so the scrollbar doesn't sit on top of the nav labels.
    paddingRight: '2px',
  },
  sectionGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--text-tertiary)',
    letterSpacing: '0.06em',
    padding: '16px 12px 8px',
    textTransform: 'uppercase' as const,
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '9px 12px',
    borderRadius: '8px',
    color: 'var(--text-secondary)',
    textDecoration: 'none',
    fontSize: '13.5px',
    fontWeight: 500,
    transition: 'background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease',
  },
  // Nav item label — single line, truncating with an ellipsis when badges eat
  // into the 260px row. Without nowrap the label wraps to two lines instead
  // (the "Sample Requests" + count badge case).
  navLabel: {
    flex: 1,
    minWidth: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Label block for NavLink rows: truncating text + optional count badge
  // glued to the end of the word, vertically centered with the text.
  // overflow/ellipsis live on the inner text span (NOT here) so the badge
  // isn't clipped.
  navLabelWrap: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
  },
  navLabelText: {
    minWidth: 0,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Notification count — small pink pill right after the label text
  // (client request), vertically centered on the text line.
  countBadge: {
    fontSize: '9px',
    fontWeight: 700,
    color: '#fff',
    background: '#FB3AAE',
    minWidth: 14,
    height: 14,
    padding: '0 4px',
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    flexShrink: 0,
  },
  // Active nav state — softened from the previous full-pink-fill which made
  // every active page feel shouty. Subtle white tint as the bg + brighter
  // text + pink icon (icon color comes from the per-link Icon render). This
  // matches the active-state pattern Linear / Notion / Vercel use: the page
  // you're ON should feel "selected," not "highlighted with neon."
  navActive: {
    background: 'var(--sidebar-accent-bg)',
    color: 'var(--sidebar-accent-text)',
    fontWeight: 600,
  },
  // Trailing badge cluster on a nav row (count + New pill). Tight internal
  // gap so two pills don't blow the 260px row width.
  badgeCluster: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  badge: {
    fontSize: '10px',
    fontWeight: 500,
    color: 'var(--text-tertiary)',
    background: '#1a1a24',
    padding: '2px 6px',
    borderRadius: '6px',
    letterSpacing: '0.02em',
    flexShrink: 0,
  },
  navFeatured: {
    background: 'rgba(251, 58, 174, 0.08)',
    border: '1px solid rgba(251, 58, 174, 0.22)',
    color: 'var(--text-primary)',
    fontWeight: 600,
  },
  badgeFeatured: {
    fontSize: '10px',
    fontWeight: 700,
    color: '#FB3AAE',
    background: 'rgba(251, 58, 174, 0.14)',
    border: '1px solid rgba(251, 58, 174, 0.30)',
    padding: '1.5px 7px',
    borderRadius: '999px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    flexShrink: 0,
  },
  comingSoonPill: {
    fontSize: '9.5px',
    fontWeight: 600,
    color: '#FB3AAE',
    background: 'rgba(251, 58, 174, 0.10)',
    border: '1px solid rgba(251, 58, 174, 0.25)',
    padding: '1.5px 7px',
    borderRadius: '999px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  bottom: {
    padding: '8px 2px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    // Pinned to the bottom-left corner — never scrolls with the nav.
    flexShrink: 0,
  },
  signOut: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '8px 12px',
    background: 'none',
    border: 'none',
    color: 'var(--text-tertiary)',
    fontSize: '13px',
    cursor: 'pointer',
    borderRadius: '8px',
    transition: 'all 0.12s ease',
    fontFamily: 'inherit',
  },
  // Brand menu popover (anchored above the brand row at bottom of sidebar).
  // Shadow uses two layers — a tight ambient + a wider diffuse — for a
  // proper floating-card look that reads as "elevated above the sidebar"
  // rather than "another sidebar element."
  brandMenuPopover: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: 0,
    right: 0,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-primary)',
    borderRadius: 10,
    padding: 6,
    boxShadow: '0 1px 2px rgba(0,0,0,0.3), 0 12px 32px rgba(0,0,0,0.45)',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    zIndex: 200,
    // It grows UPWARD from the brand row, so with enough brands it ran off the
    // top of the screen and simply clipped — "Agency home", the active brand's
    // nested actions, whatever happened to be up there. Cap it to the space that
    // actually exists and scroll instead of disappearing.
    // 104px ≈ the brand row (~64) + the sidebar's bottom padding + the 8px gap.
    maxHeight: 'calc(100dvh - 104px)',
    overflowY: 'auto',
    // Don't hand the scroll to the page once this list hits its end.
    overscrollBehavior: 'contain',
  },
  // The brand list is the part that grows without bound, so it gets its own
  // scroller. Capping it here (rather than relying only on the popover cap) keeps
  // Privacy / Terms / Sign out below it on screen instead of pushed out of reach.
  brandMenuList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    maxHeight: 300,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    // Flex items won't shrink below their content in a column without this, which
    // would force the OUTER popover to scroll on a short viewport and give two
    // nested scrollbars. With it, the list gives up height first and handles the
    // overflow itself, so Sign out stays put.
    minHeight: 0,
    // Room for the scrollbar so it never sits on top of a brand name.
    paddingRight: 2,
    marginRight: -2,
  },
  brandMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '9px 12px',
    background: 'none',
    border: 'none',
    color: 'var(--text-primary)',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: 6,
    fontFamily: 'inherit',
    textAlign: 'left' as const,
    transition: 'background 0.12s ease',
  },
  brandMenuDivider: {
    height: 1,
    background: 'var(--border-primary)',
    margin: '4px 0',
  },
  // The ACTIVE brand row in the switcher — purple-tinted highlight so it reads
  // as "selected" (with the pink check), and its nested actions clearly belong.
  brandMenuItemActive: {
    background: 'rgba(139, 92, 246, 0.16)',
    fontWeight: 600,
  },
  // Nested brand-scoped actions card, indented under the active brand. A vertical
  // gradient bar + tinted card visually bind Billing/Team/Activity/Settings to
  // the selected brand above.
  brandScopeWrap: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 8,
    margin: '3px 2px 6px 8px',
  },
  brandScopeBar: {
    width: 3,
    borderRadius: 999,
    background: 'linear-gradient(180deg, #8B5CF6 0%, #FB3AAE 100%)',
    flexShrink: 0,
  },
  brandScopeCard: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    padding: 5,
    borderRadius: 10,
    background: 'rgba(139, 92, 246, 0.07)',
    border: '1px solid rgba(139, 92, 246, 0.16)',
  },
  // "New" pill on the Team menu item — matches the featured-badge treatment.
  newBadge: {
    fontSize: '9.5px',
    fontWeight: 700,
    color: '#FB3AAE',
    background: 'rgba(251, 58, 174, 0.14)',
    border: '1px solid rgba(251, 58, 174, 0.30)',
    padding: '1.5px 6px',
    borderRadius: '999px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    flexShrink: 0,
  },
   // Small per-brand avatar in the switcher menu rows.
  menuBrandAvatar: {
    width: 20, height: 20, borderRadius: '50%',
    background: 'linear-gradient(135deg, #FB3AAE 0%, #4A90E2 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0,
  },
  menuBrandAvatarImg: {
    width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' as const,
    background: 'var(--bg-tertiary)', flexShrink: 0,
  },
}
